import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import {
  AUSTRALIAN_STATES,
  TRIP_INTEREST_IDS,
  TRIP_INTEREST_LABEL,
  TRIP_MAIN_PROGRAMME,
  TRIP_STAY_IDS,
  TRIP_STAY_LABEL,
  tripDateLabel,
  tripNights,
} from "@repo/vihangam-yog-shared";
import type { inferRouterOutputs } from "@trpc/server";
import { Check, Copy, Download, Link as LinkIcon } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import { CHAPTER } from "../chapter";
import { downloadCsv, SearchField, StatPlate } from "../components/DataKit";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

type Data = inferRouterOutputs<AppRouter>["adminVaranasiTrip"];
type Registration = Data["registrations"][number];
type Answers = Registration["answers"];
type Tally = Data["summary"]["total"];

const FORM_URL = `${CHAPTER.siteUrl}/varanasi`;

const buttonClass =
  "inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-5 text-label font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

function formatSent(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function peopleLabel(n: number): string {
  return n === 1 ? "1 person" : `${n} people`;
}

/** A WhatsApp chat link for the number they gave, when it is clearly an
 *  Australian mobile or written with its country code. */
function whatsappHref(phone: string): string | null {
  const digits = phone.replace(/\D/g, "");
  if (phone.trim().startsWith("+")) {
    return `https://wa.me/${digits}`;
  }
  if (/^04\d{8}$/.test(digits)) {
    return `https://wa.me/61${digits.slice(1)}`;
  }
  return null;
}

/** "Fri 18 Dec 2026 (5 nights)", or "" when they left the date blank. */
function departureLabel(a: Answers): string {
  if (!a.departure) {
    return "";
  }
  const nights = tripNights(a.arrival, a.departure);
  return nights === null
    ? tripDateLabel(a.departure)
    : `${tripDateLabel(a.departure)} (${nights} ${nights === 1 ? "night" : "nights"})`;
}

function interestsLabel(a: Answers): string {
  return a.interests
    .map((id) =>
      id === "other" && a.otherPlaces
        ? `${TRIP_INTEREST_LABEL.other} (${a.otherPlaces})`
        : TRIP_INTEREST_LABEL[id],
    )
    .join(", ");
}

/** A labelled bar of people, with the forms behind them, so a glance shows
 *  the spread. */
function CountRow({
  label,
  tally,
  of,
}: {
  label: string;
  tally: Tally;
  of: number;
}) {
  const share = of > 0 ? Math.round((tally.people / of) * 100) : 0;
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
      <div>
        <p className="text-body text-[color:var(--color-ink)]">{label}</p>
        <div
          aria-hidden="true"
          className="mt-1.5 h-1.5 rounded-full bg-[color:var(--color-line)]"
        >
          <div
            className="h-full rounded-full bg-[color:var(--color-amber)]"
            style={{ width: `${share}%` }}
          />
        </div>
      </div>
      <p className="text-right tabular-nums">
        <span className="text-lead font-semibold text-[color:var(--color-ink)]">
          {tally.people}
        </span>
        <span className="block text-label text-[color:var(--color-ink-soft)]">
          {tally.forms === 1 ? "1 form" : `${tally.forms} forms`}
        </span>
      </p>
    </li>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="surface p-5 md:p-6">
      <h2 className="font-display text-lead text-[color:var(--color-ink)]">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** Who arrives (or leaves) on each date, and how many haven't said. */
function DateTable({
  caption,
  dates,
  unknown,
}: {
  caption: string;
  dates: Data["summary"]["arrivals"];
  unknown: Tally;
}) {
  return (
    <table className="w-full border-collapse text-body tabular-nums">
      <caption className="pb-2 text-left font-semibold text-[color:var(--color-ink)]">
        {caption}
      </caption>
      <thead>
        <tr>
          <th className="table-head py-2 pr-3 text-left">Date</th>
          <th className="table-head py-2 text-right">People</th>
        </tr>
      </thead>
      <tbody>
        {dates.map((d) => (
          <tr
            key={d.date}
            className="border-t border-[color:var(--color-line)]"
          >
            <td className="py-2 pr-3 text-[color:var(--color-ink)]">
              {tripDateLabel(d.date)}
            </td>
            <td className="py-2 text-right font-semibold text-[color:var(--color-ink)]">
              {d.people}
            </td>
          </tr>
        ))}
        {unknown.forms > 0 ? (
          <tr className="border-t border-[color:var(--color-line)]">
            <td className="py-2 pr-3 text-[color:var(--color-ink-soft)]">
              Not sure yet
            </td>
            <td className="py-2 text-right font-semibold text-[color:var(--color-ink-soft)]">
              {unknown.people}
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

/**
 * Varanasi trip: the forms from {siteHost}/varanasi, from people thinking
 * of travelling to Swarved Mahamandir for the Varshikotsav on 14 and 15
 * December 2026. The tallies count each address's latest form and the
 * people it travels for, to plan the group's itinerary. Organisers only.
 */
export function VaranasiTrip() {
  const query = trpc.adminVaranasiTrip.useQuery(undefined, {
    retry: false,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<
    "emails" | "phones" | "link" | "failed" | null
  >(null);

  const registrations = useMemo(
    () => query.data?.registrations ?? [],
    [query.data],
  );
  const latest = useMemo(
    () => registrations.filter((r) => !r.superseded),
    [registrations],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return registrations;
    }
    return registrations.filter((r) =>
      [
        r.name,
        r.email,
        r.answers.phone,
        r.answers.city,
        r.answers.companions.join(" "),
        r.answers.extendPlaces,
        r.answers.otherPlaces,
        r.answers.comments,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [registrations, search]);

  async function copy(kind: "emails" | "phones" | "link", text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      // Clipboard blocked: say so, or they paste nothing and don't know why.
      setCopied("failed");
      window.setTimeout(() => setCopied(null), 6000);
    }
  }

  function download() {
    downloadCsv(
      "varanasi-trip-december-2026.csv",
      [
        "Sent",
        "Name",
        "Email",
        "Mobile / WhatsApp",
        "City",
        "State",
        "People",
        "Travelling with",
        "Arriving in India",
        "Leaving India",
        "Nights",
        "Days they'd like to stay",
        "Main programme",
        "Earlier or later",
        "Other places to fit in",
        "Interested in",
        "Comments",
        "Sent the form again later",
      ],
      registrations.map((r) => {
        const a = r.answers;
        return [
          r.createdAt,
          r.name,
          r.email,
          a.phone,
          a.city,
          a.state,
          a.groupSize,
          a.companions.join("; "),
          a.arrival ?? "",
          a.departure ?? "",
          tripNights(a.arrival, a.departure) ?? "",
          a.days ?? "",
          a.mainProgramme,
          a.stay ? TRIP_STAY_LABEL[a.stay] : "",
          a.extendPlaces,
          interestsLabel(a),
          a.comments,
          r.superseded ? "yes" : "",
        ];
      }),
    );
  }

  const summary = query.data?.summary;
  const stayAnswered = summary
    ? TRIP_STAY_IDS.reduce(
        (sum, id) => ({
          forms: sum.forms + summary.stay[id].forms,
          people: sum.people + summary.stay[id].people,
        }),
        { forms: 0, people: 0 },
      )
    : { forms: 0, people: 0 };

  return (
    <section className="mx-auto max-w-[1100px] px-5 py-10 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <h1 className="page-title">Varanasi trip, December 2026</h1>
        <p className="mt-3 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
          Forms from {CHAPTER.siteHost}/varanasi, from people thinking of
          travelling to Swarved Mahamandir for the Varshikotsav on 14 and 15
          December. If someone sends the form again, their latest answers are
          the ones counted.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => void copy("link", FORM_URL)}
            className={buttonClass}
          >
            {copied === "link" ? (
              <Check size={16} aria-hidden="true" />
            ) : (
              <LinkIcon size={16} aria-hidden="true" />
            )}
            {copied === "link" ? "Copied" : "Copy the form link"}
          </button>
          <a
            href={FORM_URL}
            target="_blank"
            rel="noreferrer"
            className={buttonClass}
          >
            Open the form
          </a>
        </div>
      </header>

      {query.isLoading ? (
        <p className="mt-8 text-body text-[color:var(--color-ink-soft)]">
          Loading forms…
        </p>
      ) : null}

      {query.error ? (
        <p className="mt-8 text-body text-[color:var(--color-danger)]">
          Couldn&apos;t load the forms.{" "}
          {friendlyError(
            query.error,
            "Check your internet connection and try again.",
          )}
        </p>
      ) : null}

      {copied === "failed" ? (
        <p
          role="alert"
          className="mt-3 text-body text-[color:var(--color-danger)]"
        >
          Couldn&apos;t copy. Download the spreadsheet instead.
        </p>
      ) : null}

      {summary ? (
        summary.total.forms === 0 ? (
          <p className="empty-state">
            No forms yet. Share the link above with anyone thinking of coming.
          </p>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap gap-3">
              <StatPlate
                value={summary.total.people}
                label="People travelling"
                tone="amber"
              />
              <StatPlate value={summary.total.forms} label="Forms" />
              <StatPlate
                value={summary.mainProgramme.Yes.people}
                label="Joining the programme"
              />
              <StatPlate
                value={summary.mainProgramme["Not sure yet"].people}
                label="Not sure yet"
              />
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <Panel title="The main programme, 14 and 15 December">
                <ul className="space-y-3">
                  {TRIP_MAIN_PROGRAMME.map((v) => (
                    <CountRow
                      key={v}
                      label={v}
                      tally={summary.mainProgramme[v]}
                      of={summary.total.people}
                    />
                  ))}
                </ul>
              </Panel>
              <Panel title="Arriving earlier or staying on">
                <ul className="space-y-3">
                  {TRIP_STAY_IDS.map((id) => (
                    <CountRow
                      key={id}
                      label={TRIP_STAY_LABEL[id]}
                      tally={summary.stay[id]}
                      of={summary.total.people}
                    />
                  ))}
                  {stayAnswered.forms < summary.total.forms ? (
                    <CountRow
                      label="Didn't say"
                      tally={{
                        forms: summary.total.forms - stayAnswered.forms,
                        people: summary.total.people - stayAnswered.people,
                      }}
                      of={summary.total.people}
                    />
                  ) : null}
                </ul>
              </Panel>
              <Panel title="Places and experiences">
                <ul className="space-y-3">
                  {TRIP_INTEREST_IDS.map((id) => (
                    <CountRow
                      key={id}
                      label={TRIP_INTEREST_LABEL[id]}
                      tally={summary.interests[id]}
                      of={summary.total.people}
                    />
                  ))}
                </ul>
              </Panel>
              <Panel title="When they arrive and leave">
                <div className="grid gap-6 sm:grid-cols-2">
                  <DateTable
                    caption="Arriving in India"
                    dates={summary.arrivals}
                    unknown={summary.arrivalUnknown}
                  />
                  <DateTable
                    caption="Leaving India"
                    dates={summary.departures}
                    unknown={summary.departureUnknown}
                  />
                </div>
              </Panel>
              <Panel title="Where they live">
                <ul className="space-y-3">
                  {AUSTRALIAN_STATES.filter(
                    (s) => summary.states[s].forms > 0,
                  ).map((s) => (
                    <CountRow
                      key={s}
                      label={s}
                      tally={summary.states[s]}
                      of={summary.total.people}
                    />
                  ))}
                </ul>
              </Panel>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Search names, phones, places and comments"
              />
              <button
                type="button"
                onClick={() =>
                  void copy("emails", latest.map((r) => r.email).join(", "))
                }
                className={buttonClass}
              >
                {copied === "emails" ? (
                  <Check size={16} aria-hidden="true" />
                ) : (
                  <Copy size={16} aria-hidden="true" />
                )}
                {copied === "emails" ? "Copied" : "Copy every email"}
              </button>
              <button
                type="button"
                onClick={() =>
                  void copy(
                    "phones",
                    latest.map((r) => r.answers.phone).join(", "),
                  )
                }
                className={buttonClass}
              >
                {copied === "phones" ? (
                  <Check size={16} aria-hidden="true" />
                ) : (
                  <Copy size={16} aria-hidden="true" />
                )}
                {copied === "phones" ? "Copied" : "Copy every phone number"}
              </button>
              <button type="button" onClick={download} className={buttonClass}>
                <Download size={16} aria-hidden="true" />
                Download spreadsheet
              </button>
            </div>

            <ul className="mt-6 space-y-4">
              {visible.length === 0 ? (
                <li className="empty-state">Nothing matches that search.</li>
              ) : null}
              {visible.map((r) => {
                const a = r.answers;
                const whatsapp = whatsappHref(a.phone);
                const details: [string, string][] = [
                  ["Lives in", `${a.city}, ${a.state}`],
                  ["Travelling", peopleLabel(a.groupSize)],
                  ["Travelling with", a.companions.join(", ")],
                  [
                    "Arriving in India",
                    a.arrival ? tripDateLabel(a.arrival) : "",
                  ],
                  ["Leaving India", departureLabel(a)],
                  [
                    "Days they'd like to stay",
                    a.days === null ? "" : `About ${a.days}`,
                  ],
                  ["Main programme", a.mainProgramme],
                  ["Earlier or later", a.stay ? TRIP_STAY_LABEL[a.stay] : ""],
                  ["Other places to fit in", a.extendPlaces],
                  ["Interested in", interestsLabel(a)],
                  ["Comments", a.comments],
                ];
                return (
                  <li
                    key={r.id}
                    className={`surface p-5 md:p-6 ${r.superseded ? "opacity-60" : ""}`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <p className="font-display text-lead text-[color:var(--color-ink)]">
                        {r.name}
                        <span className="ml-3 font-sans text-body text-[color:var(--color-ink-soft)]">
                          {peopleLabel(a.groupSize)}
                        </span>
                      </p>
                      <p className="text-label text-[color:var(--color-ink-soft)] tabular-nums">
                        {formatSent(r.createdAt)}
                        {r.superseded ? " · sent the form again later" : ""}
                      </p>
                    </div>
                    <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-body">
                      <a
                        href={`mailto:${r.email}`}
                        className="text-[color:var(--color-amber)] underline-offset-4 hover:underline"
                      >
                        {r.email}
                      </a>
                      <a
                        href={`tel:${a.phone.replace(/[^\d+]/g, "")}`}
                        className="text-[color:var(--color-amber)] underline-offset-4 hover:underline"
                      >
                        {a.phone}
                      </a>
                      {whatsapp ? (
                        <a
                          href={whatsapp}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[color:var(--color-amber)] underline-offset-4 hover:underline"
                        >
                          WhatsApp
                        </a>
                      ) : null}
                    </p>
                    <dl className="mt-4 grid gap-x-6 gap-y-3 text-body md:grid-cols-[12rem_minmax(0,1fr)]">
                      {details
                        .filter(([, value]) => value)
                        .map(([term, value]) => (
                          <div key={term} className="contents">
                            <dt className="font-semibold text-[color:var(--color-ink-soft)]">
                              {term}
                            </dt>
                            <dd className="whitespace-pre-wrap text-[color:var(--color-ink)]">
                              {value}
                            </dd>
                          </div>
                        ))}
                    </dl>
                  </li>
                );
              })}
            </ul>
          </>
        )
      ) : null}
    </section>
  );
}
